import { types, flow, getEnv, SnapshotOut, Instance } from 'mobx-state-tree';

import {
    translation,
} from 'easit--localization';
import { ProgressStateModel, ICommonAppEnvWithSBBaseApi } from '../index';
import { ISportsBettingBaseApi, errorIsCancel, TicketListTimeFilterRequest } from '../../api/apiBase';
import { IGroupedApiCtx } from '../../api/apiCommon';
import { ITicketListResponse, EvalState, } from '../../api/proto/apiContract';
import { SoundType } from '../../services/soundService';
import { BaseTicketListStore } from './baseTicketListStore';
import { observable } from '../../../../../node_modules/mobx';
import { translationForMts } from '../../../../localization/localization';

const ticketsPerPageForMyBets = 5;

const CashoutForMyTicket = types.model('CashoutForMyTicket', {
    id: types.number,
    value: types.number
})

/**
 * Today tickets store.
 *
 * It searches for won tickets and can play sound using SoundService.
 */
export const TodayTicketsStore =
    types.compose(BaseTicketListStore,
        types.model({
            pagesLoadedCnt: types.optional(types.number, 1),
            cashouts: types.maybeNull(types.array(CashoutForMyTicket))
        })).views(self => {
            return {
                get showLoadNext() {
                    return self.pagesLoadedCnt * ticketsPerPageForMyBets < self.totalTicketsCount;
                }
            }
        }).actions(self => {
            const env = getEnv<ICommonAppEnvWithSBBaseApi>(self);
            const ticketsPerPage = 5;

            let synchroFullCtx: IGroupedApiCtx<ISportsBettingBaseApi> | null = null;
            const ticketsToCheckWinForSound = new Set<string>();

            /**
             * isReload
             * isSilentReload is reload from timer
             */
            const loadDataAsync: (isReload: boolean, isSilentReload?: boolean) => Promise<void> = flow(function* (isReload: boolean, isSilentReload?: boolean) {

                if (env.loggedUserId <= 0) {
                    self.state = ProgressStateModel.Failed;
                    self.error = translationForMts().myTickets_Login;
                    return;
                }

                if (isSilentReload) {
                    if (synchroFullCtx && synchroFullCtx.isOpen()) {
                        return;
                    }
                }

                let ctx: IGroupedApiCtx<ISportsBettingBaseApi> | null = null;
                try {

                    if (synchroFullCtx && synchroFullCtx.isOpen()) {
                        synchroFullCtx.cancel();
                        synchroFullCtx = null;
                    }

                    ctx = env.api.open();
                    synchroFullCtx = ctx;

                    if (!isSilentReload) {
                        self.state = ProgressStateModel.Loading;
                    }

                    self.error = null;

                    const response: ITicketListResponse = yield ctx.api.getTicketListAsync({
                        timeFilter: TicketListTimeFilterRequest.Today,
                        indexOfPage: 1,
                        rowsOfPage: self.pagesLoadedCnt * ticketsPerPage,
                    });

                    self.totalTicketsCount = response.TotalCount;
                    self.setTickets(response.Items);

                    setCashoutToTickets();

                    self.state = ProgressStateModel.Success;
                    checkTicketsForSound();
                } catch (error) {
                    if (errorIsCancel(error)) {
                        throw error;
                    }
                    env.loggingService.error(error);
                    if (!isReload && !isSilentReload) {
                        self.state = ProgressStateModel.Failed;
                    }
                    self.error = translation().bet_ErrorInTicketListLoad;
                    throw error;
                } finally {
                    if (synchroFullCtx === ctx) {
                        synchroFullCtx = null;
                    }
                }
            });


            const loadNextAsync: () => Promise<void> = () => {
                self.pagesLoadedCnt += 1;
                return loadDataAsync(false);
            };

            const checkTicketsForSound = (): void => {
                let playWinSound = false;
                self.tickets.forEach(ticket => {
                    const ticketId = ticket.id.toString();
                    if (ticket.evalState === EvalState.Win || ticket.evalState === EvalState.Cashout) {
                        if (ticketsToCheckWinForSound.has(ticketId)) {
                            playWinSound = true;
                            ticketsToCheckWinForSound.delete(ticketId);
                        }
                        return;
                    }
                    ticketsToCheckWinForSound.add(ticketId);
                });

                if (playWinSound) {
                    env.soundService.playSound(SoundType.CouponWin);

                    env.couponSent({ isForUpdateBalanceOnly: true, isLive: false });
                }
            }

            const setCashouts = (cashouts: { id: number, value: number }[]) => {
                if (cashouts) {
                    self.cashouts = observable([]);
                    cashouts.forEach(cashout => {
                        self.cashouts.push(CashoutForMyTicket.create(<SnapshotOut<typeof CashoutForMyTicket>>{
                            id: cashout.id,
                            value: cashout.value
                        }))
                    })
                }
            }

            const setCashoutToTickets = () => {
                if (self.cashouts && self.cashouts.length) {
                    self.tickets.forEach(ticket => {
                        let cashout = self.cashouts.find(cashout => cashout.id === ticket.id);
                        if (cashout) {
                            ticket.cashout = cashout.value;
                        }
                        else {
                            ticket.cashout = 0;
                        }
                    })
                }
            }

            return {
                loadDataAsync,
                loadNextAsync,
                setCashouts,
                setCashoutToTickets
            }
        });

export type ITodayTicketsStore = Instance<typeof TodayTicketsStore>;
